import {ChangeDetectionStrategy, Component, Injectable, input, Signal} from "@angular/core";
import {GruppeId, GruppeMedlemListeModell, HtmlTekst, MuterendeOppgaveSpesifikasjon, OppgaveModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Temporal} from "temporal-polyfill";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {nyUuid} from "felles-ng2/støttefunksjoner/uuidGenerator";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {MatError, MatFormField, MatLabel, MatPrefix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {TranslateModule} from "@ngx-translate/core";
import {VelgMedlemKomponent} from "web-ng2/delt/komponenter/velg-medlem/velg-medlem";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";
import {Tidssoneinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {oppgaveFristSomPlainDateUTC, oppgaveFristSomSerialisertUTC} from "felles-ng2/modell/oppgaveModellHjelpere";
import {filter, Observable, switchMap} from "rxjs";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";
import PlainDate = Temporal.PlainDate;

const debug = initDebugger('OppgaveSkjemaKomponent');


export type SkjemaType = ReturnType<typeof OppgaveSkjemaKomponent.opprettSkjema>;
export type SkjemaVerdier = SkjemaType['value'];


/**
 * Skjema for å opprette / redigere oppgave
 */
@Component({
    selector: 'oppgave-skjema',
    templateUrl: 'oppgave-skjema.html',
    styleUrl: 'oppgave-skjema.scss',
    host: {'data-testid': 'oppgave-skjema'},
    imports: [
        FormsModule,
        MatDatepicker,
        MatDatepickerToggle,
        MatError,
        MatFormField,
        MatInput,
        MatLabel,
        MatPrefix,
        ReactiveFormsModule,
        StandardValideringsfeilPipe,
        TranslateModule,
        VelgMedlemKomponent,
        MatDatepickerInput,
        RiktekstRedigeringKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OppgaveSkjemaKomponent {

    static opprettSkjema() {
        const skjema = new FormGroup({
            id: new FormControl(nyUuid(), {nonNullable: true}), //ikke eksponert for redigering
            tittel: new FormControl(''), //validering settes av settFelterPåkrevd()
            beskrivelse: new FormControl<HtmlTekst | null>(null, {validators: FeltValidatorer.htmlTekst}),
            ansvarlig: new FormControl<GruppeMedlemListeModell | null>(null), //validering settes av settFelterPåkrevd()
            frist: new FormControl<PlainDate | null>(null),
        });
        OppgaveSkjemaKomponent.settFelterPåkrevd(skjema);
        return skjema;
    }

    static populerSkjema(skjema: SkjemaType, oppgave: OppgaveModell): SkjemaType {
        skjema.patchValue({
            ...oppgave,
            frist: oppgaveFristSomPlainDateUTC(oppgave)
        });
        return skjema;
    }

    /** Endrer hvorvidt fekter som i utgangspunktet er påkrevd skal være påkrevd eller ei. */
    static settFelterPåkrevd(skjema: SkjemaType, påkrevd = true) {
        if (påkrevd) {
            skjema.controls.tittel.addValidators(FeltValidatorer.litenIkkeTomStreng);
            skjema.controls.ansvarlig.addValidators(Validators.required);
        } else {
            skjema.controls.tittel.removeValidators(FeltValidatorer.litenIkkeTomStreng);
            skjema.controls.ansvarlig.removeValidators(Validators.required);
        }
    }

    static somSpesifikasjon(skjema: SkjemaType, tidssone: Tidssoneinformasjon): MuterendeOppgaveSpesifikasjon {
        const verdier = skjema.value;
        return {
            id: verdier.id!,
            tittel: verdier.tittel!,
            beskrivelse: verdier.beskrivelse!,
            ansvarligId: verdier.ansvarlig!.id,
            frist: oppgaveFristSomSerialisertUTC(skjema.value.frist ?? null),
            status: null,
            utfoertDato: null,
        }
    }

    skjema = input.required<SkjemaType>();
    gruppeId = input.required<GruppeId | null>();
    tittelLabel = input(this.oversettelser.oversett(i18n('Oppgavetittel')));

    tilgjengeligeMedlemmer: Signal<GruppeMedlemListeModell[]>;

    constructor(private avhengigheter: OppgaveSkjemaKomponentAvhengigheter, private oversettelser: Oversettelser) {
        this.tilgjengeligeMedlemmer = toSignal(toObservable(this.gruppeId)
            .pipe(
              filter(erIkkeNil),
              switchMap(gruppeId => this.avhengigheter.medlemmer(gruppeId))),
          {initialValue: []})
    }

    // private hjelpemetoder
}

@Injectable()
export class OppgaveSkjemaKomponentAvhengigheter {

    constructor(private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste) {
    }

    medlemmer(gruppeId: GruppeId): Observable<GruppeMedlemListeModell[]> {
        return this.gruppemedlemDatatjeneste.finnTilgjengelige(gruppeId);
    }
}
